import React, { useEffect, useState } from 'react';
import Lottie from 'lottie-react';
import { MessageCircle, X } from 'lucide-react';
import './LandingPage.css';
import Header from './Header';
import Footer from './Footer';
import QRCodeModal from './QRCodeModal';
import AnimatedText from './AnimatedText';
import demoVideo from '../assets/public-video.mp4';

interface LandingPageProps {
  onGetStarted: () => void;
  onNavigate?: (path: string) => void;
}

const LandingPage: React.FC<LandingPageProps> = ({ onGetStarted, onNavigate }) => {
  const [notificationAnimation, setNotificationAnimation] = useState(null);
  const [frameAnimation, setFrameAnimation] = useState(null);
  const [isQRModalOpen, setIsQRModalOpen] = useState(false);
  const [showBetaBanner, setShowBetaBanner] = useState(true);
  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    // Clear checkout=success parameter from URL when landing page is shown
    // (This happens when user is not logged in, so they can't access /people anyway)
    // This prevents infinite redirect loops after logout
    const params = new URLSearchParams(window.location.search);
    if (params.get('checkout') === 'success') {
      // Remove the checkout parameter from URL to prevent redirect loops
      params.delete('checkout');
      const newUrl = window.location.pathname + (params.toString() ? `?${params.toString()}` : '');
      window.history.replaceState({}, '', newUrl);
    }
  }, []);

  const handleQRCodeClick = () => {
    setIsQRModalOpen(true);
  };

  const closeQRModal = () => {
    setIsQRModalOpen(false);
  };

  const closeBetaBanner = () => {
    setShowBetaBanner(false);
  };

  useEffect(() => {
    // Load the Lottie animations
    fetch('/Notification-[remix].json')
      .then(response => response.json())
      .then(data => setNotificationAnimation(data))
      .catch(error => console.error('Error loading notification animation:', error));

    fetch('/Frame-1.json')
      .then(response => response.json())
      .then(data => setFrameAnimation(data))
      .catch(error => console.error('Error loading frame animation:', error));
  }, []);
  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -100px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-on-scroll');
          observer.unobserve(entry.target);
        }
      });
    }, observerOptions);

    const animateElements = document.querySelectorAll('.animate-section');
    animateElements.forEach(el => observer.observe(el));

    return () => observer.disconnect();
  }, []);
  return (
    <div 
      className="landing-page"
      style={{
        '--landscape-bg': 'url(/landscape-background.jpg)',
        '--cta-bg': 'url(/cta-background.jpg)'
      } as React.CSSProperties}
    >
      {/* Beta Banner */}
      {showBetaBanner && (
        <div className="beta-banner">
          <div className="beta-banner-content">
            <span className="beta-banner-text">🚀 We're onboarding beta users!</span>
            <button className="beta-banner-close" onClick={closeBetaBanner}>
              <X size={16} />
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      <Header 
        onNavigate={(path) => {
          if (onNavigate) {
            onNavigate(path);
          } else if (path === '/auth' || path === '/login' || path === '/register') {
            onGetStarted();
          }
        }} 
      />
      
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-content">
          <div className="hero-text">
            <h1 className="hero-title">
              <span className="hero-title-prefix">Your</span>
                  <AnimatedText words={['SalesCRM,', 'Reminders,', 'Bestfriend,', 'Assisstant,']} duration={2500} />
              <span className="hero-title-suffix"> On Text.</span>
            </h1>
            <p className="hero-subtitle">
              Remi sits on top of all your platforms and makes them usable through text (iMessage and SMS).
            </p>
            <div className="cta-container">
              <button className="cta-button" onClick={handleQRCodeClick}>
                <MessageCircle size={16} />
                Text Remi on Your Phone
              </button>
              <p className="cta-offering-text">Offering by Mantisflow Inc.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Demo Video Section */}
      <section className="demo-video-section">
        <div className="demo-video-content">
          <div className="demo-video-header">
            <h2 className="how-to-title" style={{ textAlign: 'center', width: '100%' }}>
              Squeak Peek 👀
            </h2>
          </div>
          <video
            className="demo-video"
            src={demoVideo}
            controls
            autoPlay
            loop
            playsInline
            muted
          >
            Your browser does not support the video tag.
          </video>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="how-to-section">
        <div className="how-to-content">
          <div className="how-to-header">
            <h2 className="how-to-title">How Does Remi Work?</h2>
          </div>
          
          <div className="how-to-steps">
            <div className="how-to-step">
              <div className="step-image-container">
                <img 
                  src="/Frame 2 (5)-portrait.png" 
                  alt="Message Remi"
                  style={{ width: '300px', height: 'auto' }}
                />
              </div>
              <div className="step-text-container">
                <h3 className="step-title">Message Remi with any task</h3>
                <p className="step-description">Tell Remi what you need—schedule a meeting, add someone to your network, or check your pipeline status. It's that simple.</p>
              </div>
            </div>

            <div className="how-to-step">
              <div className="step-image-container">
                <img 
                  src="/Frame 6 (3).png" 
                  alt="Remi updates your platforms"
                  style={{ width: '300px', height: 'auto', borderRadius: '4px' }}
                />
              </div>
              <div className="step-text-container">
                <h3 className="step-title">Remi updates your platforms</h3>
                <p className="step-description">Remi handles all the busywork—syncing data across your tools, updating records, and keeping everything organized automatically.</p>
              </div>
            </div>

            <div className="how-to-step">
              <div className="step-image-container">
                <img 
                  src="/Frame 4 (1)-portrait.png" 
                  alt="Stay updated and on track"
                  style={{ width: '300px', height: 'auto' }}
                />
              </div>
              <div className="step-text-container">
                <h3 className="step-title">Stay updated and on track</h3>
                <p className="step-description">Get timely reminders about meetings, network updates, and important tasks—all delivered right to your messages.</p>
              </div>
            </div>
          </div>
        </div>
      </section>





      {/* Image Section */}
      <section 
        className="image-section"
        style={{
          backgroundImage: 'var(--cta-bg)'
        }}
      >
        <div className="image-section-content">
          <h2 className="image-section-title">
            Ready to <span className="italic-bold-text">start?</span>
          </h2>
          <button className="image-section-button" onClick={handleQRCodeClick}>
            <MessageCircle size={16} />
            Text Remi on Your Phone
          </button>
        </div>
        
        {/* Footer */}
        <Footer 
          onNavigate={onNavigate || (() => {})} 
        />
      </section>
      
      {/* QR Code Modal */}
      <QRCodeModal isOpen={isQRModalOpen} onClose={closeQRModal} />
    </div>
  );
};

export default LandingPage;

